import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  HiSearch,
  HiLocationMarker,
  HiClock,
  HiCheckCircle,
  HiXCircle,
  HiPencil,
  HiTrash,
  HiCalendar,
  HiTruck,
  HiCube,
  HiArchive,
  HiDocumentText,
  HiShieldCheck,
  HiCash,
  HiExclamationCircle,
  HiArrowLeft,
  HiUser,
  HiCollection
} from 'react-icons/hi';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import { useNotification } from '../hooks/useNotification';
import Notification from './Notification';
import Select from './Select';
import './Tracking.css';

const Tracking = () => {
  const [searchParams] = useSearchParams();
  const { notifications, removeNotification, showWarning, showError } = useNotification();
  const codeFromUrl = searchParams.get('code');
  const [trackingCode, setTrackingCode] = useState(codeFromUrl || '');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceStatuses, setInvoiceStatuses] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      await loadInvoices();
      await loadInvoiceStatuses();
      if (codeFromUrl) {
        setTimeout(() => {
          handleSearchByCode(codeFromUrl);
        }, 100);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromUrl]);

  const loadInvoiceStatuses = async () => {
    try {
      const data = await apiService.getInvoiceStatus();
      const statusesList = data?.data || normalizeApiData(data);
      setInvoiceStatuses(Array.isArray(statusesList) ? statusesList : []);
    } catch (error) {
      console.error('Erro ao carregar status de invoice:', error);
      setInvoiceStatuses([]);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await apiService.getInvoicesList();
      const invoicesList = normalizeApiData(data);
      return invoicesList;
    } catch (error) {
      console.error('Erro ao carregar invoices:', error);
      return [];
    }
  };

  const handleSearchByCode = async (code) => {
    if (!code || !code.trim()) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.getTracking(code.trim());
      // A API retorna { data: { codigo_rastreio, status_geral, historico, cliente_destinatario } }
      const data = response?.data || response;
      setTrackingData(data);
    } catch (error) {
      console.error('Erro ao buscar rastreamento:', error);
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  // Função para obter o nome do status pelo ID
  const getStatusNameById = (statusId) => {
    if (!statusId) return 'Desconhecido';
    const statusIdNum = typeof statusId === 'string' ? parseInt(statusId) : statusId;
    const statusObj = invoiceStatuses.find(s => s.id === statusIdNum);
    return statusObj ? statusObj.status_invoice : `Status ${statusId}`;
  };

  // Função para obter a cor do status
  const getStatusColor = (statusId) => {
    if (!statusId) return '#6b7280';
    const statusIdNum = typeof statusId === 'string' ? parseInt(statusId) : statusId;
    const statusObj = invoiceStatuses.find(s => s.id === statusIdNum);
    const statusName = statusObj ? statusObj.status_invoice?.toLowerCase() : '';

    // Mapear cores baseado no nome do status
    if (statusName.includes('nova') || statusName.includes('novo')) {
      return '#3b82f6'; // Azul
    }
    if (statusName.includes('progresso') || statusName.includes('andamento') || statusName.includes('aguardando')) {
      return '#f59e0b'; // Laranja/Amarelo
    }
    if (statusName.includes('concluíd') || statusName.includes('entregue') || statusName.includes('confirmada')) {
      return '#10b981'; // Verde
    }
    if (statusName.includes('cancelad')) {
      return '#ef4444'; // Vermelho
    }
    if (statusName.includes('transito') || statusName.includes('trânsito') || statusName.includes('embarcado')) {
      return '#3b82f6'; // Azul
    }
    if (statusName.includes('alfândega') || statusName.includes('aduana') || statusName.includes('document')) {
      return '#f59e0b'; // Laranja
    }
    if (statusName.includes('consolida') || statusName.includes('embalad')) {
      return '#8b5cf6'; // Roxo
    }
    if (statusName.includes('extraviad') || statusName.includes('avariad') || statusName.includes('devolv')) {
      return '#ef4444'; // Vermelho
    }

    // Default
    return '#3b82f6';
  };

  const handleSearch = () => {
    if (!trackingCode.trim()) {
      showWarning('Digite um código de rastreamento');
      return;
    }

    handleSearchByCode(trackingCode);
  };

  const handleUpdateStepStatus = async (stepId, newStatus) => {
    try {
      await apiService.updateTrackingStep(stepId, { status: newStatus });
      const code = trackingData?.codigo_rastreio || trackingData?.codigo_rastreamento || trackingData?.codigo;
      await handleSearchByCode(code);
    } catch (error) {
      console.error('Erro ao atualizar etapa:', error);
      showError(error.message || 'Erro ao atualizar etapa');
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (window.confirm('Tem certeza que deseja excluir esta etapa?')) {
      try {
        await apiService.deleteTrackingStep(stepId);
        const code = trackingData?.codigo_rastreio || trackingData?.codigo_rastreamento || trackingData?.codigo;
        await handleSearchByCode(code);
      } catch (error) {
        console.error('Erro ao excluir etapa:', error);
        showError(error.message || 'Erro ao excluir etapa');
      }
    }
  };

  const getStatusIcon = (status) => {
    // Se status é um ID numérico, buscar nome na lista de invoiceStatuses
    if (status && (typeof status === 'number' || /^\d+$/.test(String(status)))) {
      const statusIdNum = typeof status === 'string' ? parseInt(status) : status;
      const statusObj = invoiceStatuses.find(s => s.id === statusIdNum);
      const statusName = statusObj ? statusObj.status_invoice?.toLowerCase() : '';

      // Mapear nomes de status para ícones
      if (statusName.includes('nova') || statusName.includes('novo')) {
        return <HiCollection className="icon-info" />;
      }
      if (statusName.includes('progresso') || statusName.includes('andamento')) {
        return <HiClock className="icon-warning" />;
      }
      if (statusName.includes('concluíd') || statusName.includes('entregue')) {
        return <HiCheckCircle className="icon-success" />;
      }
      if (statusName.includes('cancelad')) {
        return <HiXCircle className="icon-error" />;
      }
      if (statusName.includes('transito') || statusName.includes('trânsito')) {
        return <HiTruck className="icon-info" />;
      }
      if (statusName.includes('alfândega') || statusName.includes('aduana')) {
        return <HiShieldCheck className="icon-warning" />;
      }
      if (statusName.includes('consolida')) {
        return <HiCube className="icon-info" />;
      }
      if (statusName.includes('embalad') || statusName.includes('coletad')) {
        return <HiArchive className="icon-info" />;
      }
      if (statusName.includes('document')) {
        return <HiDocumentText className="icon-warning" />;
      }
      if (statusName.includes('tax') || statusName.includes('impost')) {
        return <HiCash className="icon-warning" />;
      }
      if (statusName.includes('retirad') || statusName.includes('disponível')) {
        return <HiLocationMarker className="icon-success" />;
      }
      if (statusName.includes('devolv')) {
        return <HiArrowLeft className="icon-error" />;
      }
      if (statusName.includes('extraviad') || statusName.includes('avariad')) {
        return <HiExclamationCircle className="icon-error" />;
      }
      if (statusName.includes('ausente')) {
        return <HiUser className="icon-warning" />;
      }

      // Default para IDs numéricos
      return <HiLocationMarker className="icon-info" />;
    }

    // Fallback para strings antigas
    switch (String(status).toLowerCase()) {
      case 'concluida':
      case 'entregue':
        return <HiCheckCircle className="icon-success" />;
      case 'em_andamento':
      case 'em andamento':
        return <HiClock className="icon-warning" />;
      case 'cancelada':
      case 'cancelado':
        return <HiXCircle className="icon-error" />;
      case 'coletado':
        return <HiArchive className="icon-info" />;
      case 'em_transito':
      case 'em trânsito':
        return <HiTruck className="icon-info" />;
      case 'em_alfandega':
      case 'em alfândega':
        return <HiShieldCheck className="icon-warning" />;
      case 'em_entrega':
      case 'em entrega':
        return <HiTruck className="icon-info" />;
      case 'retida':
        return <HiExclamationCircle className="icon-error" />;
      default:
        return <HiLocationMarker className="icon-info" />;
    }
  };

  const getStatusBadge = (status) => {
    // Se status é um ID numérico, buscar nome na lista de invoiceStatuses
    if (status && (typeof status === 'number' || /^\d+$/.test(String(status)))) {
      const statusName = getStatusNameById(status);
      return <span className="badge badge-info">{statusName}</span>;
    }

    // Fallback para mapeamento antigo (strings)
    const statusMap = {
      coletado: { label: 'Coletado', class: 'badge-success' },
      em_transito: { label: 'Em Trânsito', class: 'badge-info' },
      em_alfandega: { label: 'Em Alfândega', class: 'badge-warning' },
      em_entrega: { label: 'Em Entrega', class: 'badge-info' },
      entregue: { label: 'Entregue', class: 'badge-success' },
      retida: { label: 'Retida', class: 'badge-error' }
    };
    const statusInfo = statusMap[status] || statusMap.em_transito;
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };


  return (
    <div className="page-container">
      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>

      <div className="breadcrumb">
        <span>Painel</span>
        <span>Rastreamento</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Rastreamento</h1>
          <p>Rastreie suas invoices em tempo real</p>
        </div>
      </div>

      <div className="tracking-search-card">
        <div className="search-container">
          <input
            type="text"
            placeholder="Digite o código de rastreamento (ex: ST001)"
            value={trackingCode}
            onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="tracking-input"
          />
          <button className="btn btn-primary" onClick={handleSearch}>
            <HiSearch size={20} />
            Rastrear
          </button>
        </div>
      </div>

      {loading && (
        <div className="card">
          <div className="loading">Buscando informações...</div>
        </div>
      )}

      {trackingData && !loading && (
        <div className="tracking-results">
          <div className="tracking-info-header">
            <div className="tracking-info-text">
              <span className="tracking-code-simple">
                {trackingData.codigo_rastreio || trackingData.codigo_rastreamento || 'N/A'}
              </span>
              {trackingData.cliente_destinatario && trackingData.cliente_destinatario.nome && (
                <span className="tracking-client-simple">
                  {trackingData.cliente_destinatario.nome || trackingData.cliente || 'N/A'}
                </span>
              )}
              <span className="tracking-status-simple">
                {getStatusBadge(trackingData.status_geral || trackingData.status)}
              </span>
            </div>
          </div>

          <div className="card tracking-history-card">
            <div className="timeline-header-section">
              <h3 className="timeline-title">Histórico de Rastreamento</h3>
            </div>
            <div className="timeline">
              {trackingData.historico && trackingData.historico.length > 0 ? (
                trackingData.historico.map((item, index) => (
                  <div key={item.id || index} className="timeline-item">
                    <div className="timeline-marker">
                      <span className="timeline-number">{trackingData.historico.length - index}</span>
                      {getStatusIcon(item.status)}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <div className="timeline-status-wrapper">
                          <div className="timeline-status-icon-large" style={{ color: getStatusColor(item.status) }}>
                            {getStatusIcon(item.status)}
                          </div>
                          <strong className="timeline-status-text" style={{ color: getStatusColor(item.status) }}>
                            {getStatusNameById(item.status)}
                          </strong>
                        </div>
                      </div>
                      <div className="timeline-details">
                        <div className="timeline-date">
                          <HiCalendar size={16} />
                          {item.data} às {item.hora}
                        </div>
                        {item.descricao && (
                          <p className="timeline-description">
                            {item.descricao}
                          </p>
                        )}
                        {item.local_pacote && (
                          <p className="timeline-description" style={{ marginTop: '0.5rem', fontWeight: 500 }}>
                            <strong>Local do Pacote:</strong> {item.local_pacote}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : trackingData.etapas && trackingData.etapas.length > 0 ? (
                trackingData.etapas.map((item, index) => (
                  <div key={item.id} className="timeline-item">
                    <div className="timeline-marker">
                      <span className="timeline-number">{trackingData.etapas.length - index}</span>
                      {getStatusIcon(item.status)}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <div className="timeline-status-wrapper">
                          <div className="timeline-status-icon-large" style={{ color: getStatusColor(item.status) }}>
                            {getStatusIcon(item.status)}
                          </div>
                          <strong className="timeline-status-text" style={{ color: getStatusColor(item.status) }}>
                            {item.etapa}
                          </strong>
                        </div>
                        <div className="timeline-actions">
                          <Select
                            value={item.status}
                            onChange={(e) => handleUpdateStepStatus(item.id, e.target.value)}
                            className="select-inline"
                            options={[
                              { value: 'em_andamento', label: 'Em Andamento' },
                              { value: 'concluida', label: 'Concluída' },
                              { value: 'cancelada', label: 'Cancelada' }
                            ]}
                          />
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleDeleteStep(item.id)}
                            title="Editar"
                          >
                            <HiPencil size={16} />
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDeleteStep(item.id)}
                            title="Excluir"
                          >
                            <HiTrash size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="timeline-details">
                        <div className="timeline-date">
                          <HiCalendar size={16} />
                          {new Date(item.data).toLocaleDateString('pt-BR')} às {item.hora}
                        </div>
                        {item.descricao && (
                          <p className="timeline-description">
                            {item.descricao}
                          </p>
                        )}
                        {item.observacoes && (
                          <p className="timeline-observations">{item.observacoes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-timeline">
                  <p>Nenhum histórico de rastreamento disponível.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Tracking;